<template>
  <TabHeader
    :title="
      $t(
        'components.structural.organization.dialog.tabs-content.members-tab.title',
      )
    "
    :subtitle="
      $t(
        'components.structural.organization.dialog.tabs-content.members-tab.subtitle',
      )
    "
  >
    <template #end>
      <Button
        :label="
          lgAndLarger
            ? $t(
              'components.structural.organization.dialog.tabs-content.members-tab.invite.cta',
            )
            : ''
        "
        size="small"
        @click="isInviteMembersDialogVisible = true"
      >
        <template #icon>
          <LucideIcon :icon="Plus" />
        </template>
      </Button>

      <!-- TODO: Remove v-if -->
      <template v-if="members.length > 0">
        <component
          :is="overriddenInviteMembersDialogComponent"
          v-if="hasOverriddenInviteMembersDialog"
          :visible="isInviteMembersDialogVisible"
          :already-used-emails="alreadyUsedEmails"
          @hide="isInviteMembersDialogVisible = false"
        />
        <InviteMembersDialog
          v-else
          :visible="isInviteMembersDialogVisible"
          :already-used-emails="alreadyUsedEmails"
          @hide="onMembersInvited"
        />
      </template>
    </template>
  </TabHeader>

  <Tabs
    v-model:value="selectedTab"
    lazy
  >
    <TabList>
      <Tab
        v-for="tab in tabs"
        :key="tab.value"
        :value="tab.value"
      >
        <span>{{ tab.label }}</span>
        <Badge
          v-if="tab.badge"
          :value="tab.badge"
          severity="secondary"
          size="small"
          class="ml-2"
        />
      </Tab>
    </TabList>
    <TabPanels
      :pt="{
        root: 'p-0',
      }"
    >
      <TabPanel
        v-for="tab in tabs"
        :key="tab.value"
        in
        tabs
        :value="tab.value"
      >
        <component
          :is="tab.component"
          v-bind="tab.props"
        />
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<script lang="ts" setup>
import { Plus } from 'lucide-vue-next'
import { collection, query, where, doc } from 'firebase/firestore'
import MembersDataTable from './data-tables/MembersDataTable.vue'
import InvitationsDataTable from './data-tables/InvitationsDataTable.vue'
import InviteMembersDialog from './InviteMembersDialog.vue'
import type { ListOrganizationMembersData } from '~/functions/types/organization'
import type { Invitation } from '~/types/organizations/invitation'
import { CloudFunctions } from '~/constants/firebase/cloud-functions'
import { OverriddenComponents } from '~/constants/components/overrides'
import LucideIcon from '~/components/ui/LucideIcon.vue'
import TabHeader from '~/components/ui/dialog-tabs/TabHeader.vue'
import { useOrganizationsStore } from '~/store/organizations'
import { Collections } from '~/constants/firebase'
import { isComponentOverridden } from '~/utils/components'

const organizationsStore = useOrganizationsStore()
const { selectedOrganization } = storeToRefs(organizationsStore)

const isInviteMembersDialogVisible = ref(false)
const {
  isOverridden: hasOverriddenInviteMembersDialog,
  overriddenComponent: overriddenInviteMembersDialogComponent,
} = isComponentOverridden(OverriddenComponents.InviteMembersDialog)

const { data: membersData, status: membersStatus } = useAsyncData(
  'organizationMembers',
  async () => {
    const response = await useHttpsCallable<ListOrganizationMembersData>(
      CloudFunctions.ListOrganizationMembers,
      {
        organizationId: selectedOrganization.value!.id,
        orderBy: 'displayName',
        order: 'asc',
      },
    )
    return response
  },
  {
    watch: [],
  },
)
const members = computed(() => membersData.value?.data?.members ?? [])

const { data: invitations, pending: isFetchingInvitations } = useCollection<Invitation>(
  query(
    collection(useGetFirestore(), Collections.OrganizationsInvitations),
    where(
      'organization',
      '==',
      doc(
        useGetFirestore(),
        Collections.Organizations.Base,
        selectedOrganization.value!.id,
      ),
    ),
  ),
  {
    wait: true,
    ssrKey: `${Collections.OrganizationsInvitations}-${selectedOrganization.value!.id}`,
    maxRefDepth: 5,
  },
)
const alreadyUsedEmails = computed(() => [
  ...members.value.map(({ member }) => member.email),
  ...invitations.value.map(({ email }) => email),
])
const onMembersInvited = () => {
  isInviteMembersDialogVisible.value = false
  selectedTab.value = 'invitations'
}

const { t } = useI18n()
const tabs = computed(() => ([
  {
    label: t('components.structural.organization.dialog.tabs-content.members-tab.tabs.members.title'),
    value: 'members',
    component: MembersDataTable,
    props: {
      isFetching: membersStatus.value === 'pending',
      members: members.value,
    },
  },
  {
    label: t('components.structural.organization.dialog.tabs-content.members-tab.tabs.invitations.title'),
    value: 'invitations',
    badge: invitations.value.length,
    component: InvitationsDataTable,
    props: {
      isFetching: isFetchingInvitations.value,
      invitations: invitations.value,
    },
  },
]))
const selectedTab = ref(tabs.value[0].value)
</script>
